import { useMemo, useCallback } from 'react';
import { generatePath } from 'react-router-dom';
import { bpHistory } from 'global/bpHistory';
import { ROUTES_CONFIG_V2, RouteV2Id } from 'routes/routesConfigV2';
import usePathContext from 'routes/usePathContext';

type RouteParams = Record<string, string | number | undefined>;

interface UseNavigationParams {
  domainParams: Record<string, string | number | undefined>;
}

interface Breadcrumb {
  label: string;
  url: string;
}

function findMissingUrlParams(path: string, params: RouteParams): string[] {
  // match words starting with :
  return (path.match(/:\w+/g) || []).filter((p) => {
    const key = p.slice(1);
    return !params[key];
  });
}

function buildBreadcrumbs(pages: RouteV2Id[], domainParams: Record<string, string | number | undefined>): Breadcrumb[] {
  return pages.map((page) => {
    const pageConfig = ROUTES_CONFIG_V2[page];
    if (!pageConfig || !pageConfig.breadcrumb) {
      throw new Error(
        `Cannot construct breadcrumbs for page ${page}, the page or breadcrumb are missing from the routeConfigV2!`,
      );
    }
    const missingParams = findMissingUrlParams(pageConfig.path, domainParams);
    if (missingParams.length > 0) {
      throw new Error(
        `Cannot construct breadcrumbs' urls for path ${pageConfig.path}, missing params: ${missingParams.join(', ')}`,
      );
    }
    return {
      label: pageConfig.breadcrumb,
      url: generatePath(pageConfig.path, domainParams),
    };
  });
}

export function useNavigation({ domainParams }: UseNavigationParams): {
  breadcrumbs: Breadcrumb[];
  onBack: () => void;
} {
  const pathToPage = usePathContext();
  const breadcrumbs = useMemo(() => {
    return buildBreadcrumbs(pathToPage, domainParams);
  }, [pathToPage, domainParams]);

  const onBack = useCallback(() => {
    if (breadcrumbs.length > 1) {
      bpHistory.push(breadcrumbs[breadcrumbs.length - 2].url);
    }
    if (breadcrumbs.length === 1) {
      bpHistory.push(breadcrumbs[0].url);
    }
  }, [breadcrumbs]);

  return {
    breadcrumbs,
    onBack,
  };
}

export { buildBreadcrumbs };
